/**
 * Popover d'historique d'un champ profil : montre toutes les valeurs vues
 * dans KBIS/statuts/PV, triées par date la plus récente.
 */
import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History, Loader2, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

const DOC_BADGE = {
  kbis: "bg-emerald-100 text-emerald-700",
  statuts: "bg-blue-100 text-blue-700",
  pv_ago: "bg-indigo-100 text-indigo-700",
  pv_age: "bg-purple-100 text-purple-700",
  bilan: "bg-amber-100 text-amber-700",
  cession_parts: "bg-rose-100 text-rose-700",
  depot_greffe: "bg-slate-100 text-slate-700",
  autre: "bg-slate-100 text-slate-500",
};

const formatDateFR = (iso) => {
  if (!iso) return "—";
  try { const [y, m, d] = iso.slice(0, 10).split("-"); return `${d}/${m}/${y}`; } catch { return iso; }
};

const formatVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString("fr-FR");
  return String(v).slice(0, 80);
};

export default function FieldHistoryPopover({ field, currentValue, onApply }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timeline, setTimeline] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/legal/context/timeline?field=${encodeURIComponent(field)}`);
      setTimeline(r.data.timeline || []);
    } catch (e) { toast.error(apiError(e)); setTimeline([]); }
    finally { setLoading(false); }
  };

  const handleOpenChange = (o) => {
    setOpen(o);
    if (o && timeline === null) load();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="opacity-40 hover:opacity-100 transition"
          title={`Historique du champ ${field}`}
          data-testid={`field-history-btn-${field}`}>
          <History className="w-3 h-3 text-slate-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <div className="p-3 border-b bg-slate-50">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Historique · {field}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">Valeurs vues dans les documents (plus récent en premier)</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-slate-400"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>
          ) : !timeline || timeline.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">
              <Sparkles className="w-4 h-4 mx-auto mb-1 opacity-40" />
              Aucun historique. Lancez l'OCR IA sur vos KBIS/statuts/PV pour alimenter la timeline.
            </div>
          ) : (
            <div className="divide-y">
              {timeline.map((t, i) => {
                const isSameAsCurrent = String(t.value) === String(currentValue);
                return (
                  <div key={i} className="p-3 hover:bg-slate-50" data-testid={`field-history-entry-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge className={`text-[10px] ${DOC_BADGE[t.doc_type] || DOC_BADGE.autre}`}>
                        {t.doc_type}
                      </Badge>
                      <span className="text-[11px] text-slate-500">
                        {formatDateFR(t.date_document || t.uploaded_at)}
                      </span>
                      {i === 0 && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Plus récent</Badge>}
                      {isSameAsCurrent && <Badge variant="outline" className="text-[10px]">= actuel</Badge>}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-800 truncate flex-1">{formatVal(t.value)}</p>
                      {!isSameAsCurrent && onApply && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs text-blue-600"
                          onClick={() => { onApply(t.value); setOpen(false); }}
                          data-testid={`field-history-apply-${i}`}>
                          <ArrowRight className="w-3 h-3 mr-0.5" />Appliquer
                        </Button>
                      )}
                    </div>
                    {t.filename && <p className="text-[10px] text-slate-400 mt-1 truncate">{t.filename}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}


/** Petit wrapper Label + bouton historique inline. */
export function LabelWithHistory({ children, field, currentValue, onApply, className = "" }) {
  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <span className="text-sm font-medium leading-none">{children}</span>
      <FieldHistoryPopover field={field} currentValue={currentValue} onApply={onApply} />
    </div>
  );
}
